/* FeelGoodFella motion runtime
   Lenis smooth scroll + GSAP ScrollTrigger, driven by data attributes so
   sections stay editable in the theme editor.

   data-fgf-split          heading: split into lines, stagger up on load / in view
   data-fgf-reveal         fade + rise when in view (data-fgf-delay="0.2")
   data-fgf-parallax="0.2" vertical parallax, factor in viewport heights
   data-fgf-band           colour band grows from top when in view
   data-fgf-count="108"    number counts up when in view
   data-fgf-marquee        infinite marquee (child .fgf-marquee__track)
   data-fgf-hscroll        pinned horizontal scroll (child .fgf-blends__track)
   data-fgf-magnetic       magnetic hover on buttons
*/
(function () {
  'use strict';

  var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var designMode = !!(window.Shopify && window.Shopify.designMode);
  var hasGsap = typeof window.gsap !== 'undefined';
  var hasST = hasGsap && typeof window.ScrollTrigger !== 'undefined';
  var hasLenis = typeof window.Lenis !== 'undefined';

  if (!hasGsap) return;
  if (hasST) gsap.registerPlugin(ScrollTrigger);
  document.documentElement.classList.add('fgf-gsap');

  /* ---------- Lenis ---------- */
  var lenis = null;
  if (hasLenis && !reduce && !designMode) {
    lenis = new Lenis({ lerp: 0.09, smoothWheel: true, wheelMultiplier: 1 });
    window.fgfLenis = lenis;
    document.documentElement.classList.add('lenis', 'lenis-smooth');
    if (hasST) lenis.on('scroll', ScrollTrigger.update);
    gsap.ticker.add(function (t) { lenis.raf(t * 1000); });
    gsap.ticker.lagSmoothing(0);
    // keep Dawn's cart drawer / modals scrollable
    document.querySelectorAll('cart-drawer, .drawer__inner, .modal__content').forEach(function (el) {
      el.setAttribute('data-lenis-prevent', '');
    });
    // anchor links
    document.addEventListener('click', function (e) {
      var a = e.target.closest('a[href^="#"]');
      if (!a || a.getAttribute('href') === '#') return;
      var target = document.querySelector(a.getAttribute('href'));
      if (target) { e.preventDefault(); lenis.scrollTo(target, { offset: -80 }); }
    });
  }

  /* ---------- inView: IntersectionObserver-driven enter / leave ----------
     fgfInView(el, onEnter, { margin, amount, once }) -> stop()
     onEnter may return a function which runs on leave. Same contract as
     Motion's inView(), without the dependency. Exposed on window.fgfInView. */
  var ioCache = {};
  function fgfInView(el, onEnter, opts) {
    opts = opts || {};
    var key = (opts.margin || '0px') + '|' + (opts.amount || 0);
    if (!ioCache[key]) {
      var handlers = new Map();
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (en) {
          var h = handlers.get(en.target); if (!h) return;
          if (en.isIntersecting) {
            if (h.once && h.done) return;
            h.leave = h.enter(en) || null; h.done = true;
            if (h.once) io.unobserve(en.target);
          } else if (h.leave) { h.leave(en); h.leave = null; }
        });
      }, { rootMargin: opts.margin || '0px', threshold: opts.amount || 0 });
      ioCache[key] = { io: io, handlers: handlers };
    }
    var c = ioCache[key];
    c.handlers.set(el, { enter: onEnter, once: !!opts.once });
    c.io.observe(el);
    return function () { c.io.unobserve(el); c.handlers.delete(el); };
  }
  window.fgfInView = fgfInView;
  var VIEW = { margin: '-8% 0px -8% 0px' };

  function onReady(fn) {
    if (document.readyState !== 'loading') fn(); else document.addEventListener('DOMContentLoaded', fn);
  }

  onReady(function () {
    if (reduce) return; // everything is visible at rest; nothing else to do
    // pinned blend cards move horizontally; keep them visible once shown
    document.querySelectorAll('[data-fgf-hscroll] [data-fgf-reveal], .fgf-hero [data-fgf-reveal], .fgf-pillars [data-fgf-reveal]').forEach(function (el) { el.setAttribute('data-fgf-keep', ''); });

    /* ---------- Split headings ---------- */
    document.querySelectorAll('[data-fgf-split]').forEach(function (el) {
      var lines = el.querySelectorAll('.fgf-line > span');
      if (!lines.length) return;
      gsap.set(lines, { yPercent: 110 });
      var tl = gsap.to(lines, { yPercent: 0, duration: 1.1, ease: 'power4.out', stagger: 0.09, paused: true });
      if (el.hasAttribute('data-fgf-immediate')) {
        tl.delay(0.15).play();
      } else {
        fgfInView(el, function () { tl.play(); }, { margin: '0px 0px -5% 0px', once: true });
      }
    });

    /* ---------- Cycling hero words: char blur reveal, colour per word ---------- */
    document.querySelectorAll('[data-fgf-cycle]').forEach(function (el) {
      var words = Array.prototype.slice.call(el.querySelectorAll('.fgf-cycle__word'));
      if (!words.length) return;
      var interval = parseFloat(el.getAttribute('data-fgf-interval') || '3');
      var section = el.closest('.fgf-hero') || el;
      var cur = section.querySelector('[data-fgf-cycle-current]');
      var bar = section.querySelector('[data-fgf-cycle-bar]');
      var idx = 0;
      function show(i) {
        var w = words[i], chars = w.querySelectorAll('i');
        words.forEach(function (x) { x.classList.remove('is-active'); });
        w.classList.add('is-active');
        gsap.fromTo(chars, { opacity: 0, y: '0.2em', filter: 'blur(10px)' }, { opacity: 1, y: 0, filter: 'blur(0px)', duration: 0.7, ease: 'power3.out', stagger: 0.03 });
        if (cur) cur.textContent = ('0' + (i + 1)).slice(-2);
        if (bar) gsap.fromTo(bar, { scaleY: 0 }, { scaleY: 1, duration: interval, ease: 'none' });
      }
      function hide(i, done) {
        gsap.to(words[i].querySelectorAll('i'), { opacity: 0, y: '-0.2em', filter: 'blur(6px)', duration: 0.45, ease: 'power2.in', stagger: 0.02, onComplete: done });
      }
      show(0);
      if (words.length > 1) setInterval(function () {
        var prev = idx; idx = (idx + 1) % words.length;
        hide(prev, function () { show(idx); });
      }, interval * 1000);
    });

    /* ---------- Poster: builds on load, brews on scroll, reverses on the way up ---------- */
    document.querySelectorAll('[data-fgf-art]').forEach(function (art) {
      var q = function (sel) { return art.querySelectorAll(sel); };
      var blocks = q('[data-part="block"], [data-part="dot"], [data-part="table"], [data-part="mark"]');
      var leaves = q('[data-part="leaf"]'), moka = q('[data-part="moka"]'), cup = q('[data-part="cup"]'), steam = q('[data-part="steam"]'), fill = q('[data-part="fill"]'), sun = art.querySelector('.fgf-hero__sunwrap');
      steam.forEach(function (p) { var L = p.getTotalLength(); p.style.strokeDasharray = L; p.style.strokeDashoffset = L; });
      // build in
      var tl = gsap.timeline({ delay: 0.2 });
      tl.from(blocks, { scale: 0, opacity: 0, duration: 0.9, ease: 'expo.out', stagger: 0.06 }, 0)
        .from(sun ? [sun] : [], { scale: 0, opacity: 0, duration: 1.2, ease: 'expo.out' }, 0.2)
        .from(leaves, { scale: 0, rotation: -30, opacity: 0, duration: 0.9, ease: 'back.out(1.6)', stagger: 0.08 }, 0.35)
        .from(moka, { y: 60, opacity: 0, duration: 1, ease: 'expo.out' }, 0.5)
        .from(cup, { y: 40, x: 30, opacity: 0, duration: 1, ease: 'expo.out' }, 0.65)
        .set(fill, { scaleY: 0, transformOrigin: 'bottom' }, 0);
      // brew on scroll: steam draws, cup fills, sun rises, blocks drift
      if (hasST) {
        var hero = art.closest('[data-fgf-brew]') || art;
        gsap.timeline({ scrollTrigger: { trigger: hero, start: 'top top', end: 'bottom 40%', scrub: 0.6 } })
          .to(steam, { strokeDashoffset: 0, ease: 'none', stagger: 0.1 }, 0)
          .to(fill, { scaleY: 1, ease: 'none' }, 0)
          .to(sun ? [sun] : [], { y: -40, ease: 'none' }, 0)
          .to(q('[data-part="block"]'), { y: -24, ease: 'none', stagger: 0.05 }, 0)
          .to(q('[data-part="dot"]'), { y: 30, ease: 'none', stagger: 0.05 }, 0)
          .to(leaves, { rotation: 8, ease: 'none', stagger: 0.05 }, 0);
      }
      // mouse parallax on layers
      if (window.matchMedia('(hover: hover)').matches) {
        var px = gsap.quickTo(art, '--px', { duration: 0.8, ease: 'power3' });
        art.addEventListener('mousemove', function (e) {
          var r = art.getBoundingClientRect(), x = (e.clientX - r.left) / r.width - 0.5, y = (e.clientY - r.top) / r.height - 0.5;
          gsap.to(blocks, { x: x * -10, duration: 0.8, ease: 'power3', overwrite: 'auto' });
          gsap.to(leaves, { x: x * 16, y: y * 10, duration: 0.8, ease: 'power3', overwrite: 'auto' });
          gsap.to(moka, { x: x * 6, duration: 0.8, ease: 'power3', overwrite: 'auto' });
        });
      }
    });

    /* ---------- Compositions: build in, lines draw, drift on scroll ---------- */
    document.querySelectorAll('[data-fgf-comp]').forEach(function (c) {
      var circles = c.querySelectorAll('[data-k="circle"]'), planes = c.querySelectorAll('[data-k="plane"]'), lines = c.querySelectorAll('[data-k="line"]');
      lines.forEach(function (l) { try { var L = l.getTotalLength(); l.style.strokeDasharray = L; l.style.strokeDashoffset = L; } catch (e) {} });
      var tl = gsap.timeline({ paused: true });
      tl.from(planes, { scale: 0, opacity: 0, duration: 0.9, ease: 'expo.out', stagger: 0.08 }, 0)
        .from(circles, { scale: 0, duration: 1, ease: 'back.out(1.7)', stagger: 0.08 }, 0.15)
        .to(lines, { strokeDashoffset: 0, duration: 1.2, ease: 'power2.inOut', stagger: 0.06 }, 0.1);
      fgfInView(c, function () { tl.timeScale(1).play(); return function () { tl.timeScale(2).reverse(); }; }, { margin: '0px' });
      gsap.to(circles, { y: -40, ease: 'none', scrollTrigger: { trigger: c, start: 'top bottom', end: 'bottom top', scrub: true } });
      gsap.to(planes, { y: 30, ease: 'none', stagger: 0.02, scrollTrigger: { trigger: c, start: 'top bottom', end: 'bottom top', scrub: true } });
      var spin = c.querySelector('[data-k="line"]');
      if (c.classList.contains('fgf-comp--cross') && spin) gsap.to(c.querySelector('svg'), { rotation: 8, ease: 'none', scrollTrigger: { trigger: c, start: 'top bottom', end: 'bottom top', scrub: true } });
    });

    /* ---------- Bridges: cross the boundary as it passes ---------- */
    document.querySelectorAll('.fgf-bridge').forEach(function (b) {
      gsap.set(b, { scale: 0 });
      fgfInView(b, function () { gsap.to(b, { scale: 1, duration: 1, ease: 'expo.out', overwrite: 'auto' }); return function () { gsap.to(b, { scale: 0, duration: 0.5, ease: 'power2.in', overwrite: 'auto' }); }; }, { margin: '0px' });
      gsap.to(b, { yPercent: b.classList.contains('fgf-bridge--half') ? 30 : -25, ease: 'none', scrollTrigger: { trigger: b, start: 'top bottom', end: 'bottom top', scrub: true } });
    });

    /* ---------- Page construction lines draw on load ---------- */
    var gl = document.querySelectorAll('.fgf-grid-overlay i');
    if (gl.length) gsap.to(gl, { scaleY: 1, duration: 1.6, ease: 'expo.inOut', stagger: 0.15, delay: 0.3 });

    /* ---------- Strip: fades away as the hero leaves ---------- */
    var strip = document.querySelector('[data-fgf-strip]');
    var hero = document.querySelector('.fgf-hero');
    if (strip && hero && hasST) {
      gsap.from(strip, { scaleY: 0, duration: 1.4, ease: 'expo.inOut' });
      gsap.to(strip, { autoAlpha: 0, ease: 'none', scrollTrigger: { trigger: hero, start: 'bottom 100%', end: 'bottom 55%', scrub: true } });
    }

    /* ---------- Reveal ---------- */
    document.querySelectorAll('[data-fgf-reveal]').forEach(function (el) {
      var delay = parseFloat(el.getAttribute('data-fgf-delay') || '0');
      var keep = el.hasAttribute('data-fgf-keep'); // stays visible once shown
      gsap.set(el, { y: 40, autoAlpha: 0 });
      fgfInView(el, function () {
        gsap.to(el, { y: 0, autoAlpha: 1, duration: 1, ease: 'power3.out', delay: delay, overwrite: 'auto' });
        if (keep) return null;
        return function () { gsap.to(el, { y: 24, autoAlpha: 0, duration: 0.5, ease: 'power2.in', overwrite: 'auto' }); };
      }, { margin: '-6% 0px -6% 0px', once: keep });
    });

    /* ---------- Colour bands ---------- */
    document.querySelectorAll('[data-fgf-band]').forEach(function (el) {
      gsap.set(el, { scaleY: 0 });
      fgfInView(el, function () {
        gsap.to(el, { scaleY: 1, duration: 1.4, ease: 'expo.inOut', overwrite: 'auto' });
        return function () { gsap.to(el, { scaleY: 0, duration: 0.6, ease: 'power2.in', overwrite: 'auto' }); };
      }, VIEW);
    });

    if (!hasST) return;

    /* ---------- Parallax ---------- */
    document.querySelectorAll('[data-fgf-parallax]').forEach(function (el) {
      var f = parseFloat(el.getAttribute('data-fgf-parallax') || '0.15');
      gsap.fromTo(el, { yPercent: -f * 100 }, {
        yPercent: f * 100, ease: 'none',
        scrollTrigger: { trigger: el.parentElement || el, start: 'top bottom', end: 'bottom top', scrub: true }
      });
    });

    /* ---------- Counters ---------- */
    document.querySelectorAll('[data-fgf-count]').forEach(function (el) {
      var target = parseFloat(el.getAttribute('data-fgf-count') || el.textContent) || 0;
      var obj = { v: 0 };
      el.textContent = '0';
      fgfInView(el, function () {
        gsap.to(obj, { v: target, duration: 2, ease: 'power2.out', overwrite: 'auto', onUpdate: function () { el.textContent = Math.round(obj.v); } });
        return function () { gsap.to(obj, { v: 0, duration: 0.6, ease: 'power2.in', overwrite: 'auto', onUpdate: function () { el.textContent = Math.round(obj.v); } }); };
      }, VIEW);
    });

    /* ---------- Marquee ---------- */
    document.querySelectorAll('[data-fgf-marquee]').forEach(function (wrap) {
      var track = wrap.querySelector('.fgf-marquee__track');
      if (!track) return;
      var half = track.scrollWidth / 2;
      var tween = gsap.to(track, { xPercent: -50, duration: half / 90, ease: 'none', repeat: -1 });
      // speed up with scroll velocity
      ScrollTrigger.create({
        onUpdate: function (self) {
          var v = Math.min(Math.abs(self.getVelocity()) / 400, 4);
          gsap.to(tween, { timeScale: 1 + v, duration: 0.4, overwrite: true });
        }
      });
    });

    /* ---------- Blend track arrows (mobile / unpinned) ---------- */
    document.querySelectorAll('[data-fgf-hscroll]').forEach(function (section) {
      var track = section.querySelector('.fgf-blends__track');
      var prev = section.querySelector('[data-fgf-prev]'), next = section.querySelector('[data-fgf-next]');
      if (!track || !prev) return;
      var step = function () { var c = track.querySelector('.fgf-label'); return c ? c.getBoundingClientRect().width + 24 : 320; };
      prev.addEventListener('click', function () { if (window.innerWidth >= 990) { window.scrollBy({ top: -step(), behavior: 'smooth' }); } else track.scrollBy({ left: -step(), behavior: 'smooth' }); });
      next.addEventListener('click', function () { if (window.innerWidth >= 990) { window.scrollBy({ top: step(), behavior: 'smooth' }); } else track.scrollBy({ left: step(), behavior: 'smooth' }); });
    });

    /* ---------- Horizontal scroll ---------- */
    document.querySelectorAll('[data-fgf-hscroll]').forEach(function (section) {
      var track = section.querySelector('.fgf-blends__track');
      var bar = section.querySelector('.fgf-blends__progress i');
      if (!track) return;
      var mm = gsap.matchMedia();
      mm.add('(min-width: 990px)', function () {
        var dist = function () { return track.scrollWidth - window.innerWidth; };
        var tween = gsap.to(track, {
          x: function () { return -dist(); },
          ease: 'none',
          scrollTrigger: {
            trigger: section,
            pin: true,
            scrub: 0.6,
            start: 'top top',
            end: function () { return '+=' + dist(); },
            invalidateOnRefresh: true,
            onUpdate: function (self) {
              if (bar) gsap.set(bar, { x: self.progress * (bar.parentElement.clientWidth - bar.clientWidth) });
            }
          }
        });
        return function () { tween.scrollTrigger && tween.scrollTrigger.kill(); tween.kill(); };
      });
    });

    /* ---------- Manifesto: words light up with scroll ---------- */
    document.querySelectorAll('[data-fgf-words]').forEach(function (el) {
      fgfInView(el, function () { gsap.to(el, { autoAlpha: 1, duration: 0.6, overwrite: 'auto' }); return function () { gsap.to(el, { autoAlpha: 0.15, duration: 0.5, overwrite: 'auto' }); }; }, VIEW);
      gsap.to(el.querySelectorAll('.w'), {
        opacity: 1, stagger: 0.08, ease: 'none',
        scrollTrigger: { trigger: el, start: 'top 85%', end: 'center 50%', scrub: 0.3 }
      });
    });

    /* ---------- Section blending: tween the page colour as sections enter ---------- */
    var root = document.documentElement;
    var bgSections = document.querySelectorAll('[data-fgf-bg]');
    if (bgSections.length) {
      var base = { bg: getComputedStyle(root).getPropertyValue('--fgf-page-bg').trim() || '#F5F0E8', fg: getComputedStyle(root).getPropertyValue('--fgf-page-fg').trim() || '#1C1A16' };
      var apply = function (bg, fg) { gsap.to(root, { '--fgf-page-bg': bg, '--fgf-page-fg': fg, duration: 0.9, ease: 'power2.inOut', overwrite: 'auto' }); };
      bgSections.forEach(function (sec) {
        var bg = sec.getAttribute('data-fgf-bg'), fg = sec.getAttribute('data-fgf-fg') || '#1C1A16';
        ScrollTrigger.create({
          trigger: sec, start: 'top 55%', end: 'bottom 45%',
          onEnter: function () { apply(bg, fg); }, onEnterBack: function () { apply(bg, fg); },
          onLeave: function () { var n = sec.nextElementSibling; if (!n || !n.hasAttribute('data-fgf-bg')) apply(base.bg, base.fg); },
          onLeaveBack: function () { var pv = sec.previousElementSibling; if (!pv || !pv.hasAttribute('data-fgf-bg')) apply(base.bg, base.fg); }
        });
      });
    }

    /* ---------- Header: solid after the hero, hide on scroll down, read progress ---------- */
    var header = document.querySelector('[data-fgf-header]');
    if (header) {
      var wrap = header.closest('.fgf-header-wrap');
      ScrollTrigger.create({
        start: 'top -80', end: 99999,
        toggleClass: { className: 'is-scrolled', targets: wrap ? [header, wrap] : header },
        onUpdate: function (self) { header.classList.toggle('is-hidden', self.direction === 1 && self.scroll() > 400 && !header.classList.contains('is-open')); }
      });
      var prog = header.querySelector('[data-fgf-progress]');
      if (prog) gsap.to(prog, { scaleX: 1, ease: 'none', scrollTrigger: { start: 0, end: 'max', scrub: 0.3 } });
      // intro
      gsap.from(header.querySelectorAll('[data-fgf-hin]'), { autoAlpha: 0, duration: 0.7, ease: 'power2.out', stagger: 0.05, delay: 0.15 });
    }
    var ticker = document.querySelector('[data-fgf-ticker] .fgf-ticker__track');
    if (ticker) {
      gsap.to(ticker, { xPercent: -50, duration: ticker.scrollWidth / 2 / 60, ease: 'none', repeat: -1 });
    }

    /* ---------- Magnetic buttons ---------- */
    if (window.matchMedia('(hover: hover)').matches) {
      document.querySelectorAll('[data-fgf-magnetic], .fgf-btn').forEach(function (btn) {
        var xTo = gsap.quickTo(btn, 'x', { duration: 0.5, ease: 'power3' });
        var yTo = gsap.quickTo(btn, 'y', { duration: 0.5, ease: 'power3' });
        btn.addEventListener('mousemove', function (e) {
          var r = btn.getBoundingClientRect();
          xTo((e.clientX - (r.left + r.width / 2)) * 0.25);
          yTo((e.clientY - (r.top + r.height / 2)) * 0.25);
        });
        btn.addEventListener('mouseleave', function () { xTo(0); yTo(0); });
      });

      /* ---------- Cursor ---------- */
      var cur = document.createElement('div');
      cur.className = 'fgf-cursor';
      document.body.appendChild(cur);
      var cx = gsap.quickTo(cur, 'x', { duration: 0.25, ease: 'power3' });
      var cy = gsap.quickTo(cur, 'y', { duration: 0.25, ease: 'power3' });
      window.addEventListener('mousemove', function (e) { cx(e.clientX); cy(e.clientY); });
      document.addEventListener('mouseover', function (e) {
        cur.classList.toggle('is-hover', !!e.target.closest('a, button, .fgf-label, [data-fgf-magnetic]'));
      });
    }

    // Refresh after images and fonts settle so pins measure correctly
    window.addEventListener('load', function () { ScrollTrigger.refresh(); });
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(function () { ScrollTrigger.refresh(); });
  });

  /* ---------- Live open / closed status (Europe/London) ---------- */
  onReady(function () {
    var h = document.querySelector('[data-fgf-header]');
    // The header keeps a collapsed dot and the hanging strip shows the full
    // label, so there is more than one of these to keep in sync now.
    var els = [].slice.call(document.querySelectorAll('[data-fgf-status]'));
    if (!h || !els.length) return;
    var days = (h.getAttribute('data-open-days') || '1,2,3,4,5').split(',').map(function (d) { return parseInt(d, 10); });
    var from = (h.getAttribute('data-open-from') || '08:00').split(':'), to = (h.getAttribute('data-open-to') || '16:00').split(':');
    function fmt(hh, mm) { var ap = hh >= 12 ? 'pm' : 'am'; var x = hh % 12 || 12; return x + (mm ? ':' + ('0' + mm).slice(-2) : '') + ap; }
    function update() {
      // Read London weekday/hour/minute directly. Re-parsing a locale string
      // (e.g. en-GB "24/09/2026, 10:30") gives an Invalid Date, which made
      // the status read "Closed · opens tomorrow" at all times.
      var d, mins;
      try {
        var parts = {};
        new Intl.DateTimeFormat('en-GB', { timeZone: 'Europe/London', weekday: 'short', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' })
          .formatToParts(new Date()).forEach(function (p) { parts[p.type] = p.value; });
        d = { Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6, Sun: 7 }[parts.weekday];
        mins = (parseInt(parts.hour, 10) % 24) * 60 + parseInt(parts.minute, 10);
        if (!d || isNaN(mins)) throw 0;
      } catch (e) {
        var now = new Date();
        d = now.getDay() || 7; mins = now.getHours() * 60 + now.getMinutes();
      }
      var o = parseInt(from[0], 10) * 60 + parseInt(from[1] || 0, 10), c = parseInt(to[0], 10) * 60 + parseInt(to[1] || 0, 10);
      var open = days.indexOf(d) > -1 && mins >= o && mins < c;
      els.forEach(function (el) { el.classList.toggle('is-open', open); el.classList.toggle('is-closed', !open); });
      var label;
      if (open) {
        label = 'Open now · closes ' + fmt(parseInt(to[0], 10), parseInt(to[1] || 0, 10));
      } else {
        var names = ['', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
        var openTxt = fmt(parseInt(from[0], 10), parseInt(from[1] || 0, 10));
        if (days.indexOf(d) > -1 && mins < o) label = 'Closed · opens ' + openTxt;
        else {
          var n = 1; while (n < 8 && days.indexOf(((d - 1 + n) % 7) + 1) === -1) n++;
          label = 'Closed · opens ' + (n === 1 ? 'tomorrow ' : names[((d - 1 + n) % 7) + 1] + ' ') + openTxt;
        }
      }
      els.forEach(function (el) { var t = el.querySelector('span'); if (t) t.textContent = label; });
    }
    update(); setInterval(update, 60000);
  });

  /* ---------- Cart: header bag opens Dawn's drawer when present ---------- */
  onReady(function () {
    var link = document.querySelector('[data-fgf-cart]');
    if (!link) return;
    link.addEventListener('click', function (e) {
      var drawer = document.querySelector('cart-drawer');
      if (drawer && typeof drawer.open === 'function') { e.preventDefault(); drawer.open(link); }
    });
  });

  /* ---------- Mobile menu ---------- */
  onReady(function () {
    var header = document.querySelector('[data-fgf-header]');
    var toggle = document.querySelector('[data-fgf-menu-toggle]');
    var menu = document.querySelector('[data-fgf-menu]');
    if (!toggle || !menu) return;
    var lines = menu.querySelectorAll('.fgf-line > span');
    var open = false;
    function set(state) {
      open = state;
      toggle.setAttribute('aria-expanded', state);
      menu.setAttribute('aria-hidden', !state);
      header.classList.toggle('is-open', state);
      document.documentElement.classList.toggle('fgf-menu-open', state);
      if (lenis) state ? lenis.stop() : lenis.start();
      if (reduce) { menu.classList.toggle('is-open', state); return; }
      if (state) {
        menu.classList.add('is-open');
        gsap.fromTo(menu.querySelector('.fgf-menu__band'), { scaleY: 0 }, { scaleY: 1, duration: 0.8, ease: 'expo.inOut' });
        gsap.fromTo(lines, { yPercent: 110 }, { yPercent: 0, duration: 0.9, ease: 'power4.out', stagger: 0.06, delay: 0.25 });
        gsap.fromTo(menu.querySelector('.fgf-menu__foot'), { autoAlpha: 0, y: 20 }, { autoAlpha: 1, y: 0, duration: 0.6, delay: 0.6 });
      } else {
        gsap.to(lines, { yPercent: -110, duration: 0.4, ease: 'power3.in', stagger: 0.03, onComplete: function () { menu.classList.remove('is-open'); } });
      }
    }
    toggle.addEventListener('click', function () { set(!open); });
    var closeBtn = menu.querySelector('[data-fgf-menu-close]');
    if (closeBtn) closeBtn.addEventListener('click', function () { set(false); });
    menu.querySelectorAll('a').forEach(function (a) { a.addEventListener('click', function () { set(false); }); });
    document.addEventListener('keydown', function (e) { if (e.key === 'Escape' && open) set(false); });
  });

  // Theme editor: re-init on section reload
  if (designMode) {
    document.addEventListener('shopify:section:load', function () { if (hasST) ScrollTrigger.refresh(); });
  }
})();
